import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';

const INVOICE_ISSUERS = [
  { name: 'LotSense', tagline: 'Wholesale Dealer Services' },
  { name: 'Alexis Motorcar', tagline: '' },
  { name: 'AMC Wholesale', tagline: '' },
];
const INVOICE_ADDRESS = '4001 W Alexis Rd, Toledo, OH 43623';
const INVOICE_TERMS = 'Net 15';

const addNet15Days = (dateStr) => {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + 15);
  return d.toISOString().split('T')[0];
};

const getIssuerDisplay = (issuerName) =>
  INVOICE_ISSUERS.find(i => i.name === issuerName) || INVOICE_ISSUERS[0];

const InvoicesTab = ({ bankAccounts = [], onRefresh, autoOpenTrigger }) => {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPayment, setShowPayment] = useState(null);
  const [printInvoice, setPrintInvoice] = useState(null);

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (autoOpenTrigger > 0) { setEditing(null); setShowForm(true); }
  }, [autoOpenTrigger]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [inv, cl] = await Promise.all([
        api.request('/invoices'),
        api.request('/invoices/clients')
      ]);
      setInvoices(inv);
      setClients(cl);
    } catch (err) {
      console.error('Failed to load invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';
  const fmt = (a) => {
    if (!a && a !== 0) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a);
  };

  const statusBadge = (status) => {
    const s = { draft: 'bg-neutral-100 text-neutral-600', sent: 'bg-blue-100 text-blue-700', partial: 'bg-amber-100 text-amber-700', paid: 'bg-green-100 text-green-700', void: 'bg-red-100 text-red-600' };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${s[status] || s.draft}`}>{(status||'draft').charAt(0).toUpperCase()+(status||'draft').slice(1)}</span>;
  };

  const filtered = invoices.filter(inv => {
    if (filter === 'unpaid' && !['draft','sent','partial'].includes(inv.status)) return false;
    if (filter === 'paid' && inv.status !== 'paid') return false;
    if (search) {
      const s = search.toLowerCase();
      return inv.clientName?.toLowerCase().includes(s) || inv.invoiceNumber?.toLowerCase().includes(s) || inv.serviceDescription?.toLowerCase().includes(s);
    }
    return true;
  });

  const handleDelete = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoiceNumber}?`)) return;
    try { await api.request(`/invoices/${inv.id}`, { method: 'DELETE' }); loadData(); } catch (err) { alert('Failed: ' + err.message); }
  };

  // PRINT
  const handlePrint = (invoice) => {
    const w = window.open('', '_blank', 'width=800,height=600');
    const issuer = getIssuerDisplay(invoice.issuerName || 'LotSense');
    const items = (invoice.lineItems||[]).map(li => `<tr><td style="padding:8px;border-bottom:1px solid #f1f5f9;font-size:13px">${li.description}</td><td style="padding:8px;border-bottom:1px solid #f1f5f9;font-size:13px;text-align:center">${li.quantity}</td><td style="padding:8px;border-bottom:1px solid #f1f5f9;font-size:13px;text-align:right">${fmt(li.unitPrice)}</td><td style="padding:8px;border-bottom:1px solid #f1f5f9;font-size:13px;text-align:right;font-weight:500">${fmt(li.amount)}</td></tr>`).join('');
    const bal = (parseFloat(invoice.total)||0) - (parseFloat(invoice.amountPaid)||0);
    const paidSection = (parseFloat(invoice.amountPaid)||0) > 0 ? `<div style="display:flex;justify-content:space-between;font-size:13px;color:#16a34a;margin-top:4px"><span>Paid</span><span>${fmt(invoice.amountPaid)}</span></div>${bal > 0 ? `<div style="display:flex;justify-content:space-between;font-size:14px;font-weight:bold;color:#dc2626;margin-top:4px"><span>Balance Due</span><span>${fmt(bal)}</span></div>` : ''}` : '';
    const dueDate = invoice.dueDate || addNet15Days(invoice.invoiceDate?.split('T')[0] || new Date().toISOString().split('T')[0]);
    w.document.write(`<html><head><title>Invoice ${invoice.invoiceNumber}</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,sans-serif;padding:40px;color:#333}@media print{body{padding:20px}}</style></head><body>
      <div style="display:flex;justify-content:space-between;margin-bottom:24px"><div><div style="font-size:22px;font-weight:bold;color:#0f172a">${issuer.name}</div>${issuer.tagline ? `<div style="font-size:12px;color:#666;margin-top:4px">${issuer.tagline}</div>` : ''}<div style="font-size:12px;color:#666;margin-top:6px">${INVOICE_ADDRESS}</div></div><div style="text-align:right"><div style="font-size:18px;font-weight:bold;color:#2563eb">${invoice.invoiceNumber}</div><div style="font-size:12px;color:#666">Date: ${formatDate(invoice.invoiceDate)}</div><div style="font-size:12px;color:#666">Due: ${formatDate(dueDate)}</div><div style="font-size:12px;color:#666;margin-top:2px">Terms: ${INVOICE_TERMS}</div></div></div>
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:14px;margin-bottom:16px"><div style="font-size:11px;color:#94a3b8;text-transform:uppercase;font-weight:600;margin-bottom:4px">Bill To</div><div style="font-size:15px;font-weight:600">${invoice.clientName}</div>${invoice.clientContact?`<div style="font-size:13px;color:#64748b">${invoice.clientContact}</div>`:''}</div>
      ${invoice.serviceDescription?`<div style="font-size:13px;color:#475569;margin-bottom:16px;padding:10px;background:#f1f5f9;border-radius:6px"><strong>Service:</strong> ${invoice.serviceDescription}</div>`:''}
      <table style="width:100%;border-collapse:collapse;margin-bottom:16px"><thead><tr><th style="text-align:left;padding:8px;background:#f1f5f9;border-bottom:2px solid #e2e8f0;font-size:11px;text-transform:uppercase;color:#64748b">Description</th><th style="text-align:center;padding:8px;background:#f1f5f9;border-bottom:2px solid #e2e8f0;font-size:11px;text-transform:uppercase;color:#64748b;width:60px">Qty</th><th style="text-align:right;padding:8px;background:#f1f5f9;border-bottom:2px solid #e2e8f0;font-size:11px;text-transform:uppercase;color:#64748b;width:100px">Unit Price</th><th style="text-align:right;padding:8px;background:#f1f5f9;border-bottom:2px solid #e2e8f0;font-size:11px;text-transform:uppercase;color:#64748b;width:100px">Amount</th></tr></thead><tbody>${items}</tbody></table>
      <div style="border-top:2px solid #0f172a;padding-top:10px;margin-bottom:16px"><div style="display:flex;justify-content:space-between;font-size:16px;font-weight:bold"><span>Total</span><span>${fmt(invoice.total)}</span></div>${paidSection}</div>
      ${invoice.notes?`<div style="font-size:12px;color:#64748b;padding:10px;background:#fefce8;border-radius:6px;border:1px solid #fef08a"><strong>Notes:</strong> ${invoice.notes}</div>`:''}
      <div style="margin-top:30px;text-align:center;font-size:11px;color:#94a3b8;border-top:1px solid #e2e8f0;padding-top:12px">Thank you for your business!</div>
    <script>window.onload=function(){window.print()}</script></body></html>`);
    w.document.close();
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-neutral-500">Loading invoices...</div></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl font-bold">Invoices</h2>
        <div className="flex items-center gap-3">
          <input type="text" placeholder="Search client, invoice #..." value={search} onChange={e => setSearch(e.target.value)} className="px-3 py-2 border border-neutral-300 rounded-lg text-sm w-60" />
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">+ New Invoice</button>
        </div>
      </div>
      <div className="flex gap-2">
        {['all','unpaid','paid'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 text-sm rounded-lg border ${filter===f?'bg-neutral-800 text-white border-neutral-800':'bg-white text-neutral-600 border-neutral-200 hover:bg-neutral-50'}`}>
            {f==='all'?`All (${invoices.length})`:f==='unpaid'?`Unpaid (${invoices.filter(i=>['draft','sent','partial'].includes(i.status)).length})`:`Paid (${invoices.filter(i=>i.status==='paid').length})`}
          </button>
        ))}
      </div>
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Invoice #</th>
              <th className="px-4 py-3 text-left font-medium">Client</th>
              <th className="px-4 py-3 text-left font-medium">Service</th>
              <th className="px-4 py-3 text-left font-medium">Date</th>
              <th className="px-4 py-3 text-right font-medium">Total</th>
              <th className="px-4 py-3 text-right font-medium">Paid</th>
              <th className="px-4 py-3 text-center font-medium">Status</th>
              <th className="px-4 py-3 text-center font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-neutral-500">{search ? 'No matching invoices' : 'No invoices yet'}</td></tr>
            ) : filtered.map(inv => {
              const bal = (parseFloat(inv.total)||0) - (parseFloat(inv.amountPaid)||0);
              return (
                <tr key={inv.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3 font-mono font-medium text-blue-600">{inv.invoiceNumber}</td>
                  <td className="px-4 py-3 font-medium">{inv.clientName}</td>
                  <td className="px-4 py-3 text-neutral-500 truncate max-w-[200px]">{inv.serviceDescription || '-'}</td>
                  <td className="px-4 py-3">{formatDate(inv.invoiceDate)}</td>
                  <td className="px-4 py-3 text-right font-medium">{fmt(inv.total)}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={inv.status==='paid'?'text-green-600':'text-amber-600'}>{fmt(inv.amountPaid||0)}</span>
                    {bal > 0 && inv.status !== 'draft' && <div className="text-xs text-red-500">Due: {fmt(bal)}</div>}
                  </td>
                  <td className="px-4 py-3 text-center">{statusBadge(inv.status)}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => handlePrint(inv)} className="px-2 py-1 text-xs bg-neutral-100 rounded hover:bg-neutral-200" title="Print">🖨</button>
                      <button onClick={() => { setEditing(inv); setShowForm(true); }} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200">Edit</button>
                      {inv.status !== 'paid' && <button onClick={() => setShowPayment(inv)} className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200">Pay</button>}
                      <button onClick={() => handleDelete(inv)} className="px-2 py-1 text-xs bg-red-100 text-red-600 rounded hover:bg-red-200">Del</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && <InvoiceFormModal invoice={editing} clients={clients} bankAccounts={bankAccounts} fmt={fmt} onClose={() => { setShowForm(false); setEditing(null); }} onSave={loadData} />}
      {showPayment && <PaymentModal invoice={showPayment} fmt={fmt} onClose={() => setShowPayment(null)} onSave={loadData} onRefresh={onRefresh} />}
    </div>
  );
};

// ===================== INVOICE FORM MODAL =====================
const InvoiceFormModal = ({ invoice, clients, bankAccounts, fmt, onClose, onSave }) => {
  const defaultDate = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    clientName: invoice?.clientName || '', clientContact: invoice?.clientContact || '',
    serviceDescription: invoice?.serviceDescription || '',
    invoiceDate: invoice?.invoiceDate?.split('T')[0] || defaultDate,
    dueDate: invoice?.dueDate?.split('T')[0] || addNet15Days(invoice?.invoiceDate?.split('T')[0] || defaultDate),
    notes: invoice?.notes || '',
    bankAccountId: invoice?.bankAccountId || '', status: invoice?.status || 'draft',
    issuerName: invoice?.issuerName || 'LotSense',
  });
  const [lineItems, setLineItems] = useState(
    invoice?.lineItems?.length > 0
      ? invoice.lineItems.map(li => ({ description: li.description||'', quantity: li.quantity||1, unitPrice: li.unitPrice||0, amount: li.amount||0 }))
      : [{ description: '', quantity: 1, unitPrice: 0, amount: 0 }]
  );
  const [saving, setSaving] = useState(false);
  const [showSugg, setShowSugg] = useState(false);

  const updateLI = (idx, field, value) => {
    setLineItems(prev => {
      const u = [...prev]; u[idx] = { ...u[idx], [field]: value };
      if (field === 'quantity' || field === 'unitPrice') {
        const q = field === 'quantity' ? parseFloat(value)||0 : parseFloat(u[idx].quantity)||0;
        const p = field === 'unitPrice' ? parseFloat(value)||0 : parseFloat(u[idx].unitPrice)||0;
        u[idx].amount = Math.round(q * p * 100) / 100;
      }
      return u;
    });
  };

  const subtotal = lineItems.reduce((s, li) => s + (parseFloat(li.amount)||0), 0);
  const suggestions = form.clientName.length > 0 ? clients.filter(c => c.clientName?.toLowerCase().includes(form.clientName.toLowerCase())) : [];

  const handleSave = async (asStatus) => {
    if (!form.clientName.trim()) { alert('Client name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, status: asStatus || form.status, lineItems: lineItems.filter(li => li.description.trim()) };
      if (invoice?.id) {
        await api.request(`/invoices/${invoice.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        await api.request('/invoices', { method: 'POST', body: JSON.stringify(payload) });
      }
      onSave(); onClose();
    } catch (err) { alert('Failed: ' + err.message); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-neutral-200 flex justify-between items-center">
          <h2 className="text-lg font-bold">{invoice?.id ? 'Edit Invoice' : 'New Invoice'}</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl">×</button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Invoice From</label>
            <select value={form.issuerName} onChange={e => setForm({ ...form, issuerName: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 rounded-lg">
              {INVOICE_ISSUERS.map(i => (
                <option key={i.name} value={i.name}>{i.name}</option>
              ))}
            </select>
            <p className="text-xs text-neutral-500 mt-1">{INVOICE_ADDRESS} · Terms: {INVOICE_TERMS}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-sm font-medium text-neutral-700 mb-1">Client Name *</label>
              <input type="text" value={form.clientName} onChange={e => { setForm({...form, clientName: e.target.value}); setShowSugg(true); }} onBlur={() => setTimeout(() => setShowSugg(false), 200)} className="w-full px-3 py-2 border border-neutral-300 rounded-lg" placeholder="Dealer or customer name" />
              {showSugg && suggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {suggestions.map((c, i) => (
                    <button key={i} onClick={() => { setForm({...form, clientName: c.clientName, clientContact: c.clientContact||form.clientContact}); setShowSugg(false); }} className="w-full text-left px-3 py-2 hover:bg-neutral-50 text-sm">{c.clientName}</button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Contact</label>
              <input type="text" value={form.clientContact} onChange={e => setForm({...form, clientContact: e.target.value})} className="w-full px-3 py-2 border border-neutral-300 rounded-lg" placeholder="Phone / email" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Service Description</label>
            <input type="text" value={form.serviceDescription} onChange={e => setForm({...form, serviceDescription: e.target.value})} className="w-full px-3 py-2 border border-neutral-300 rounded-lg" placeholder="e.g. Title work, customer repair" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="block text-sm font-medium text-neutral-700 mb-1">Invoice Date</label><input type="date" value={form.invoiceDate} onChange={e => { const d = e.target.value; setForm({ ...form, invoiceDate: d, dueDate: addNet15Days(d) }); }} className="w-full px-3 py-2 border border-neutral-300 rounded-lg" /></div>
            <div><label className="block text-sm font-medium text-neutral-700 mb-1">Due Date</label><input type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 rounded-lg" /><p className="text-xs text-neutral-400 mt-0.5">{INVOICE_TERMS}</p></div>
            <div><label className="block text-sm font-medium text-neutral-700 mb-1">Bank Account</label><select value={form.bankAccountId} onChange={e => setForm({...form, bankAccountId: e.target.value})} className="w-full px-3 py-2 border border-neutral-300 rounded-lg"><option value="">-- Select --</option>{bankAccounts.map(ba => <option key={ba.id} value={ba.id}>{ba.name}</option>)}</select></div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Line Items</label>
            <div className="border border-neutral-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50"><tr><th className="px-3 py-2 text-left font-medium">Description</th><th className="px-3 py-2 text-center font-medium w-20">Qty</th><th className="px-3 py-2 text-center font-medium w-28">Price</th><th className="px-3 py-2 text-right font-medium w-24">Amount</th><th className="w-10"></th></tr></thead>
                <tbody>
                  {lineItems.map((li, idx) => (
                    <tr key={idx} className="border-t border-neutral-100">
                      <td className="px-2 py-1"><input type="text" value={li.description} onChange={e => updateLI(idx,'description',e.target.value)} className="w-full px-2 py-1 border border-neutral-200 rounded" placeholder="Description" /></td>
                      <td className="px-2 py-1"><input type="number" value={li.quantity} onChange={e => updateLI(idx,'quantity',e.target.value)} className="w-full px-2 py-1 border border-neutral-200 rounded text-center" min="0" /></td>
                      <td className="px-2 py-1"><input type="number" value={li.unitPrice} onChange={e => updateLI(idx,'unitPrice',e.target.value)} className="w-full px-2 py-1 border border-neutral-200 rounded text-right" min="0" step="0.01" /></td>
                      <td className="px-3 py-1 text-right font-mono">{fmt(li.amount)}</td>
                      <td className="px-1 py-1 text-center">{lineItems.length > 1 && <button onClick={() => setLineItems(p=>p.filter((_,i)=>i!==idx))} className="text-red-400 hover:text-red-600 text-lg">×</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-3 py-2 border-t border-neutral-100 flex justify-between items-center">
                <button onClick={() => setLineItems(p=>[...p,{description:'',quantity:1,unitPrice:0,amount:0}])} className="text-sm text-blue-600 hover:text-blue-700 font-medium">+ Add Line</button>
                <div><span className="text-sm text-neutral-500 mr-3">Total:</span><span className="text-lg font-bold">{fmt(subtotal)}</span></div>
              </div>
            </div>
          </div>
          <div><label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label><textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm" rows={2} placeholder="Optional notes" /></div>
        </div>
        <div className="p-5 border-t border-neutral-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50">Cancel</button>
          <button onClick={() => handleSave('draft')} disabled={saving} className="px-4 py-2 bg-neutral-600 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50">{saving?'Saving...':'Save Draft'}</button>
          <button onClick={() => handleSave('sent')} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{saving?'Saving...':'Save & Mark Sent'}</button>
        </div>
      </div>
    </div>
  );
};

// ===================== PAYMENT MODAL =====================
const PaymentModal = ({ invoice, fmt, onClose, onSave, onRefresh }) => {
  const [form, setForm] = useState({ amountPaid: invoice?.amountPaid || invoice?.total || 0, paymentMethod: invoice?.paymentMethod || '', paymentDate: invoice?.paymentDate?.split('T')[0] || new Date().toISOString().split('T')[0] });
  const [saving, setSaving] = useState(false);
  const total = parseFloat(invoice?.total)||0;
  const paid = parseFloat(form.amountPaid)||0;
  const balance = total - paid;

  const handleSave = async () => {
    setSaving(true);
    try { await api.request(`/invoices/${invoice.id}/payment`, { method: 'PUT', body: JSON.stringify(form) }); onSave(); onClose(); if (onRefresh) onRefresh(); } catch (err) { alert('Failed: ' + err.message); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-neutral-200"><h2 className="text-lg font-bold">Record Payment</h2><p className="text-sm text-neutral-500">{invoice.invoiceNumber} — {invoice.clientName}</p></div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-neutral-700 mb-1">Amount Paid</label><div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">$</span><input type="number" value={form.amountPaid} onChange={e => setForm({...form, amountPaid: e.target.value})} className="w-full pl-7 pr-3 py-2 border border-neutral-300 rounded-lg" step="0.01" /></div></div>
            <div><label className="block text-sm font-medium text-neutral-700 mb-1">Method</label><select value={form.paymentMethod} onChange={e => setForm({...form, paymentMethod: e.target.value})} className="w-full px-3 py-2 border border-neutral-300 rounded-lg"><option value="">-- Select --</option><option value="Cash">Cash</option><option value="Check">Check</option><option value="ACH">ACH</option><option value="Wire">Wire</option><option value="Credit Card">Credit Card</option><option value="Zelle">Zelle</option><option value="Venmo">Venmo</option></select></div>
          </div>
          <div><label className="block text-sm font-medium text-neutral-700 mb-1">Payment Date</label><input type="date" value={form.paymentDate} onChange={e => setForm({...form, paymentDate: e.target.value})} className="w-full px-3 py-2 border border-neutral-300 rounded-lg" /></div>
          <div className="bg-neutral-100 rounded-lg p-4">
            <div className="flex justify-between text-sm"><span>Invoice Total:</span><span className="font-medium">{fmt(total)}</span></div>
            <div className="flex justify-between text-sm"><span>Amount Paid:</span><span className="font-medium text-green-600">{fmt(paid)}</span></div>
            <div className="flex justify-between text-sm font-bold border-t border-neutral-300 pt-2 mt-2"><span>Balance:</span><span className={balance>0?'text-red-600':'text-green-600'}>{fmt(balance)}</span></div>
          </div>
        </div>
        <div className="p-5 border-t border-neutral-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">{saving?'Saving...':'Record Payment'}</button>
        </div>
      </div>
    </div>
  );
};

export default InvoicesTab;
